"use client";
import { useState, useEffect, useCallback } from 'react';
import { WpSidebarMenu } from '@/lib/wp/types';
import { SidebarTranslations } from '@/lib/translations/types';

interface ProcessedMenuItem {
  id: string;
  label: string;
  url: string;
  children: ProcessedMenuItem[];
  articles: ProcessedMenuItem[];
}

interface AccordionNavigationClientProps {
  menu: WpSidebarMenu | null;
  translations: SidebarTranslations;
  currentCategory?: string;
}

export default function AccordionNavigationClient({ 
  translations
}: AccordionNavigationClientProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydration-safe initialization
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Debounce search query for performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 150);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get processed menu data from server (with URLs already computed)
  const processedMenu = useCallback(() => {
    if (!isHydrated) return [];
    
    const navElement = document.querySelector('[data-accordion-navigation]');
    if (!navElement) return [];
    
    const menuData = navElement.getAttribute('data-processed-menu');
    if (!menuData) return [];
    
    try {
      return JSON.parse(menuData);
    } catch (error) {
      console.error('Failed to parse processed menu data:', error);
      return [];
    }
  }, [isHydrated]);

  // Filter articles based on debounced search query
  const filteredArticles = useCallback(() => {
    if (!debouncedSearchQuery.trim()) return [];

    const allArticles: { id: string; label: string; url: string }[] = [];
    
    processedMenu().forEach((category: ProcessedMenuItem) => {
      allArticles.push(...category.articles);
    });

    return allArticles.filter(article =>
      article.label.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
    );
  }, [processedMenu, debouncedSearchQuery]);

  // Toggle accordion item with keyboard support
  const toggleItem = useCallback((itemId: string, event?: Event) => {
    if (event && event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    
    if (event) {
      event.preventDefault();
    }
    
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: Event, itemId: string) => {
    if (event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ')) {
      toggleItem(itemId, event);
    }
  }, [toggleItem]);

  // Handle main accordion toggle with keyboard support
  const handleMainToggle = useCallback((event?: Event) => {
    if (event && event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    
    if (event) {
      event.preventDefault();
    }
    
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  // Handle search input change
  const handleSearchChange = useCallback((event: Event) => {
    const target = event.target as HTMLInputElement;
    setSearchQuery(target.value);
  }, []);


  // Add interactivity to existing server-rendered elements
  useEffect(() => {
    if (!isHydrated) return;

    // Add main accordion toggle handler
    const accordionToggle = document.querySelector('[data-accordion-toggle]');
    if (accordionToggle) {
      accordionToggle.addEventListener('click', handleMainToggle);
      accordionToggle.addEventListener('keydown', handleMainToggle);
    }

    // Add accordion item click handlers
    const accordionItems = document.querySelectorAll('[data-accordion-item]');
    
    accordionItems.forEach(item => {
      const itemId = item.getAttribute('data-accordion-item');
      if (!itemId) return;
      
      const clickHandler = () => {
        toggleItem(itemId);
      };
      
      const keydownHandler = (e: Event) => {
        handleKeyDown(e, itemId);
      };
      
      item.addEventListener('click', clickHandler);
      item.addEventListener('keydown', keydownHandler);
      
      // Store handlers for cleanup
      (item as HTMLElement & { _clickHandler?: () => void; _keydownHandler?: (e: Event) => void })._clickHandler = clickHandler;
      (item as HTMLElement & { _clickHandler?: () => void; _keydownHandler?: (e: Event) => void })._keydownHandler = keydownHandler;
    });

    // Add search input handler
    const searchInput = document.getElementById('article-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', handleSearchChange);
    }

    return () => {
      // Cleanup event listeners
      if (accordionToggle) {
        accordionToggle.removeEventListener('click', handleMainToggle);
        accordionToggle.removeEventListener('keydown', handleMainToggle);
      }
      
      accordionItems.forEach(item => {
        const clickHandler = (item as HTMLElement & { _clickHandler?: () => void })._clickHandler;
        const keydownHandler = (item as HTMLElement & { _keydownHandler?: (e: Event) => void })._keydownHandler;
        
        if (clickHandler) {
          item.removeEventListener('click', clickHandler);
        }
        if (keydownHandler) {
          item.removeEventListener('keydown', keydownHandler);
        }
      });

      if (searchInput) {
        searchInput.removeEventListener('input', handleSearchChange);
      }
    };
  }, [isHydrated, handleMainToggle, toggleItem, handleKeyDown, handleSearchChange]);

  // Update UI based on state
  useEffect(() => {
    if (!isHydrated) return;

    // Update main accordion visibility
    const navigationContent = document.getElementById('navigation-content');
    const accordionToggle = document.querySelector('[data-accordion-toggle]');
    
    if (navigationContent && accordionToggle) {
      if (isExpanded) {
        navigationContent.classList.remove('hidden');
        navigationContent.classList.add('block');
        accordionToggle.setAttribute('aria-expanded', 'true');
        accordionToggle.setAttribute('aria-label', `${translations.browseArticles}, expanded`);
      } else {
        navigationContent.classList.remove('block');
        navigationContent.classList.add('hidden');
        accordionToggle.setAttribute('aria-expanded', 'false');
        accordionToggle.setAttribute('aria-label', `${translations.browseArticles}, collapsed`);
      }
    }

    // Update accordion items
    const accordionItems = document.querySelectorAll('[data-accordion-item]');
    accordionItems.forEach(item => {
      const itemId = item.getAttribute('data-accordion-item');
      const submenu = document.getElementById(`submenu-${itemId}`);
      const isExpanded = expandedItems.has(itemId || '');
      
      // Only update submenu if it exists
      if (submenu) {
        if (isExpanded) {
          submenu.classList.remove('hidden');
          submenu.classList.add('block');
        } else {
          submenu.classList.remove('block');
          submenu.classList.add('hidden');
        }
      }

      if (item) {
        item.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        const hasChildren = item.getAttribute('aria-controls') !== null;
        item.setAttribute('aria-label', `${item.getAttribute('data-accordion-item')}${hasChildren ? `, ${isExpanded ? 'expanded' : 'collapsed'}` : ''}`);
      }
    });

    // Update search results and hide/show accordion items
    const searchResultsContainer = document.querySelector('[data-search-results]');
    const regularMenuContainer = document.querySelector('[data-regular-menu]') as HTMLElement;
    
    if (searchQuery.trim()) {
      // Hide regular menu when searching
      if (regularMenuContainer) {
        regularMenuContainer.style.display = 'none';
      }
      
      const filteredArticlesList = filteredArticles();
      
      if (searchResultsContainer) {
        // Clear existing content safely
        while (searchResultsContainer.firstChild) {
          searchResultsContainer.removeChild(searchResultsContainer.firstChild);
        }
        
        if (filteredArticlesList.length > 0) {
          // Show search results using safe DOM manipulation
          const container = document.createElement('div');
          container.className = 'p-2';
          
          const innerDiv = document.createElement('div');
          innerDiv.className = 'space-y-1';
          
          const headerDiv = document.createElement('div');
          headerDiv.className = 'px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide';
          headerDiv.setAttribute('aria-live', 'polite');
          headerDiv.textContent = `${translations.searchResults} (${filteredArticlesList.length})`;
          
          const ul = document.createElement('ul');
          ul.setAttribute('role', 'list');
          ul.setAttribute('aria-label', 'Search results');
          
          filteredArticlesList.forEach(article => {
            const li = document.createElement('li');
            li.setAttribute('key', article.id);
            
            const link = document.createElement('a');
            link.href = article.url;
            link.className = 'block p-3 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors';
            link.setAttribute('aria-label', `Read article: ${article.label}`);
            link.setAttribute('title', `Read: ${article.label}`);
            link.setAttribute('rel', 'nofollow');
            link.textContent = article.label;
            
            li.appendChild(link);
            ul.appendChild(li);
          });
          
          innerDiv.appendChild(headerDiv);
          innerDiv.appendChild(ul);
          container.appendChild(innerDiv);
          searchResultsContainer.appendChild(container);
        } else {
          // Show no results using safe DOM manipulation
          const noResultsDiv = document.createElement('div');
          noResultsDiv.className = 'p-4 text-center text-gray-500 text-sm';
          noResultsDiv.setAttribute('role', 'status');
          noResultsDiv.setAttribute('aria-live', 'polite');
          noResultsDiv.textContent = `${translations.noResults} "${searchQuery}"`;
          
          searchResultsContainer.appendChild(noResultsDiv);
        }
      }
    } else {
      // Show regular menu when not searching
      if (regularMenuContainer) {
        regularMenuContainer.style.display = 'block';
      }
      
      if (searchResultsContainer) {
        // Clear search results safely
        while (searchResultsContainer.firstChild) {
          searchResultsContainer.removeChild(searchResultsContainer.firstChild);
        }
      }
    }
  }, [isHydrated, isExpanded, expandedItems, searchQuery, filteredArticles, translations]);

  // This component only handles interactivity - no JSX return needed
  return null;
}
